import { useState, useEffect, useRef } from "react";
import { Bell, Check, ArrowDownToLine, ArrowUpFromLine, Info } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";

const ICONS = { deposit: ArrowDownToLine, withdraw: ArrowUpFromLine, info: Info };

export default function NotificationBell() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const pollUnread = () =>
    api.get("/notifications/unread").then(({ data }) => setUnread(data.count || 0)).catch(() => {});

  useEffect(() => {
    pollUnread();
    const id = setInterval(pollUnread, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        if (unread > 0) { await api.post("/notifications/read"); setUnread(0); }
      } catch {}
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        data-testid="notification-bell-btn"
        title={t("notifications")}
        className="relative grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-amber-500/40 bg-black/30 text-amber-300 hover:border-amber-400 transition-colors"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 grid place-items-center h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-2xl border border-amber-500/30 bg-[#12131d] shadow-2xl z-[70] overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Bell size={15} className="text-amber-400" />
            <span className="font-display font-bold text-sm">{t("notifications")}</span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-slate-500">{t("noNotifications")}</p>
            )}
            {items.map((n) => {
              const Ic = ICONS[n.type] || Info;
              return (
                <div key={n.id} className={`px-4 py-3 flex gap-3 ${n.read ? "" : "bg-amber-500/5"}`} data-testid={`notification-item-${n.id}`}>
                  <div className={`shrink-0 grid place-items-center h-8 w-8 rounded-lg ${n.type === "withdraw" ? "bg-cyan-500/15 text-cyan-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                    <Ic size={15} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-100">{n.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5 break-words">{n.message}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
